import { apiClient, BaseAPIService } from './client';

// Plan 222: every Tier-1 metrics payload reports how the internal-account
// exclusion resolved. "applied" = internal accounts excluded (clean default);
// "included_by_request" = include_internal=true, intentional; "lookup_failed" =
// the users.is_internal lookup broke, so internal accounts are NOT excluded and
// the dashboard must warn (fail-visible, never silently "clean").
export type InternalFilterStatus = 'applied' | 'included_by_request' | 'lookup_failed';

export interface MetricsSummary {
  total_runs: number;
  overall_success_rate: number;
  avg_quality_score: number | null;
  p05_quality_score: number | null;
  median_quality_score: number | null;
  avg_cost: number | null;
  p95_cost: number | null;
  avg_execution_time_ms: number | null;
  p95_execution_time_ms: number | null;
  avg_latency_per_1k_chars: number | null;
  p95_latency_per_1k_chars: number | null;
  avg_citation_coverage: number | null;
  // Clause density (informational, not a quality signal)
  avg_clause_coverage: number | null;

  // Extraction quality - pipeline population (analysed docs)
  extraction_quality_pipeline: {
    avg_boundary_confidence_mean: number | null;
    avg_ensemble_confidence: number | null;
    avg_low_confidence_rate: number | null;
    avg_boundary_p10: number | null;
    catastrophic_failure_rate: number;
    avg_empty_clause_rate: number | null;
    semantic_layer_rate: number;
    vision_layer_rate: number;
  };

  // Extraction quality - upload population (all uploaded docs, current state)
  extraction_quality_uploads: {
    total_documents: number;
    near_zero_extractions: number;
    near_zero_rate: number | null;
    escalation_rate_semantic: number | null;
    escalation_rate_vision: number | null;
  };
  avg_invalid_citation_rate: number | null;
  avg_hallucination_rate: number | null;
  // Plan 186 task 2.1: global Evidence denominators per metric
  findings_total: number;
  findings_with_citations_total: number;
  findings_checked_for_grounding: number;
  citation_refs_total: number;
  invalid_citation_refs_total: number;
  by_workflow: Record<string, {
    count: number;
    success_rate: number;
    avg_quality: number | null;
    p05_quality: number | null;
    total_cost: number;
    avg_cost: number | null;
    p95_cost: number | null;
    users: Array<{
      user_id: string;
      email: string;
      run_count: number;
      success_rate: number;
      total_cost: number;
      avg_cost: number;
    }>;
    // Plan 186 task 2.1: per-workflow Evidence breakdown
    evidence: {
      citation_coverage: number | null;
      invalid_citation_rate: number | null;
      hallucination_rate: number | null;
      findings_checked_n: number;
      findings_count: number;
    } | null;
  }>;
  // Plan 186 task 2.2: Chat Quality panel
  chat_quality: {
    total_messages: number;
    avg_chunks_retrieved: number | null;
    avg_mean_similarity: number | null;
    avg_topk_spread: number | null;
    refusal_count: number;
    refusal_rate: number | null;
  };
  // Plan 186 task 2.4: Drafting panel
  drafting_quality: {
    total_runs: number;
    avg_clauses_generated: number | null;
    p95_clauses_generated: number | null;
    template_counts: Record<string, number>;
  };
  // Plan 186 task 2.3: Reaped runs in last 24h
  reaped_count: number;
  // Plan 186 task 2.6: Documents panel
  documents_overview: {
    period_days: number;
    total: number;
    by_type: Record<string, number>;
    by_size_bucket: Record<string, number>;
    completed_count: number;
    failed_count: number;
    processing_count: number;
    pending_over_5m_count: number;
    pending_over_20m_count: number;
    unknown_status_count: number;
    failure_rate: number;
  } | null;
  // Plan 186 task 1.8: Metrics Coverage card
  metrics_completeness: {
    expected: number;
    actual: number;
    missing: number;
    missing_pct: number;
    completeness_pct: number;
    status: 'green' | 'yellow' | 'red' | 'unknown';
    missing_sample: string[];
    stale_count: number;
    stale_sample: string[];
  };
  top_failures: Array<{ reason: string; count: number }>;
  cost_summary: {
    total_spend: number;
    avg_cost_per_clause: number | null;
    by_workflow: Record<string, number>;
  };
  scoring_version: string;
  period_days: number;
  // Plan 222: scopes the Tier-1 trust-signal fields ONLY. The nested Tier-2
  // sub-objects (reaped_count, metrics_completeness, documents_overview,
  // extraction_quality_uploads) are NOT internal-filtered.
  internal_filter_status: InternalFilterStatus;
  warning?: string;
}

// Plan 186 task 1.5: shadow detection sample row
export interface ShadowDetectionRow {
  prompt: string;
  confidence: number | null;
  reasoning: string;
  threat_type: string | null;
  created_at: string;
}

export interface ShadowDetections {
  period_days: number;
  limit: number;
  rows: ShadowDetectionRow[];
  internal_filter_status: InternalFilterStatus;
  warning?: string;
}

export interface TrendDay {
  date: string;
  runs: number;
  success_rate: number;
  avg_quality: number | null;
  avg_cost: number | null;
  total_cost: number;
  p95_time_ms: number | null;
}

export interface MetricsTrends {
  days: TrendDay[];
  internal_filter_status: InternalFilterStatus;
  warning?: string;
}

// Plan 111: Trust & Adoption Metrics
export interface TrustMetrics {
  period_days: number;
  trust_metrics_version: string;
  internal_filter_status: InternalFilterStatus;
  warning?: string;
  engagement: {
    total_exports: number;
    unique_exporting_users: number;
    exports_per_exporting_user: number | null;
    exports_by_format: Record<string, number>;
    exports_by_workflow: Record<string, number>;
    total_redrafts_all_sources: number;
    ai_redrafts_total: number;
    ai_redrafts_accepted: number;
    ai_redrafts_rejected: number;
    adoption_rate: number | null;
    completion_rate: number | null;
    ai_redraft_share: number | null;
    redrafts_by_source: Record<string, number>;
  };
  guardrails: {
    total_user_chat_attempts: number;
    total_blocks: number;
    block_rate: number | null;
    block_rate_pattern: number | null;
    block_rate_llm: number | null;
    blocks_by_method: Record<string, number>;
    shadow_detections: number;
    rate_limit_events: number;
    retry_after_block_count: number;
    retry_after_block_rate: number | null;
    // Plan 186: LLM Guard health bundle
    shadow_evaluation_count: number;
    shadow_detection_rate: number | null;
    shadow_detection_per_attempt: number | null;
    logged_llm_guard_evaluation_count: number;
    llm_guard_error_count: number;
    llm_guard_error_rate: number | null;
    average_llm_guard_latency_ms: number | null;
    p95_llm_guard_latency_ms: number | null;
  };
}

class AdminAPI extends BaseAPIService {
  async getMetricsSummary(days: number = 30, scoringVersion?: string, environment?: string, includeInternal?: boolean): Promise<MetricsSummary> {
    const params = new URLSearchParams({ days: String(days) });
    if (scoringVersion) params.append('scoring_version', scoringVersion);
    if (environment) params.append('environment', environment);
    if (includeInternal) params.append('include_internal', 'true');
    const response = await apiClient.get(`/api/v1/admin/metrics/summary?${params}`);
    return response.data;
  }

  async getMetricsTrends(days: number = 30, environment?: string, includeInternal?: boolean): Promise<MetricsTrends> {
    const params = new URLSearchParams({ days: String(days) });
    if (environment) params.append('environment', environment);
    if (includeInternal) params.append('include_internal', 'true');
    const response = await apiClient.get(`/api/v1/admin/metrics/trends?${params}`);
    return response.data;
  }

  async getTrustMetrics(days: number = 30, environment?: string, includeInternal?: boolean): Promise<TrustMetrics> {
    const params = new URLSearchParams({ days: String(days) });
    if (environment) params.append('environment', environment);
    if (includeInternal) params.append('include_internal', 'true');
    const response = await apiClient.get(`/api/v1/admin/metrics/trust?${params}`);
    return response.data;
  }

  // Plan 186 task 1.5: sample decrypted shadow detections
  async getShadowDetections(limit: number = 20, days: number = 30, environment?: string, includeInternal?: boolean): Promise<ShadowDetections> {
    const params = new URLSearchParams({
      limit: String(limit),
      days: String(days),
    });
    if (environment) params.append('environment', environment);
    if (includeInternal) params.append('include_internal', 'true');
    const response = await apiClient.get(`/api/v1/admin/metrics/shadow-detections?${params}`);
    return response.data;
  }
}

export const adminApi = new AdminAPI();
